/* ============================================================================
   NVGD — Product page controller (custom PDP).
   Plain script (no @theme module graph). Scoped per section, fully null-guarded,
   JSDoc-typed to satisfy the theme's strict checkJs (see assets/jsconfig.json).
   Responsibilities:
     1. Variant resolution from an inline JSON blob -> sync hidden name="id",
        price, installments, availability + selected-state UI, swap main image,
        announce changes to a polite live region.
     2. Quantity stepper.
     3. Gallery thumbnail swap.
     4. AJAX add-to-cart that mirrors Horizon's native flow (POST cart_add_url with
        cart-items section ids, then dispatch `cart:update` + open the cart drawer).
        Falls back to a normal form POST if anything throws or JS is off.
   No-ops gracefully in STATIC demo mode (no variants / no form).
   ============================================================================ */

/**
 * @typedef {Object} NvgdVariant
 * @property {number} id
 * @property {string[]} options
 * @property {boolean} available
 * @property {string} price
 * @property {string} installments
 * @property {string|null} media
 */

(function () {
  /** @param {HTMLElement} root */
  function init(root) {
    if (root.dataset.nvgdInit === '1') return;
    root.dataset.nvgdInit = '1';

    const form = /** @type {HTMLFormElement | null} */ (root.querySelector('form.nvgd-product__form'));
    const idInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-nvgd-id]'));
    const priceEl = root.querySelector('[data-nvgd-price]');
    const compareEl = root.querySelector('[data-nvgd-compare]');
    const discountEl = root.querySelector('[data-nvgd-discount]');
    const atcBtn = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-nvgd-atc]'));
    const atcText = root.querySelector('[data-nvgd-atc-text]');
    const mainImg = /** @type {HTMLImageElement | null} */ (root.querySelector('[data-nvgd-main-img]'));
    const liveRegion = root.querySelector('[data-nvgd-live]');

    /* ---- variant data ---- */
    /** @type {NvgdVariant[]} */
    let variants = [];
    const dataEl = root.querySelector('[data-nvgd-variants]');
    if (dataEl) {
      try { variants = JSON.parse(dataEl.textContent || '[]') || []; } catch (e) { variants = []; }
    }

    /** Announce a message to screen readers (clear-then-set so it always fires). @param {string} msg */
    function announce(msg) {
      if (!liveRegion) return;
      liveRegion.textContent = '';
      requestAnimationFrame(function () { liveRegion.textContent = msg; });
    }

    /* ---- option selection -------------------------------------------------- */
    /** @returns {string[]} */
    function selectedOptions() {
      /** @type {string[]} */
      const opts = [];
      /** @type {NodeListOf<HTMLElement>} */
      (root.querySelectorAll('[data-nvgd-optgroup]')).forEach(function (group) {
        const pos = parseInt(group.getAttribute('data-nvgd-optgroup') || '', 10);
        const checked = /** @type {HTMLInputElement | null} */ (group.querySelector('input[data-nvgd-option]:checked'));
        if (checked && pos) opts[pos - 1] = checked.value;
      });
      return opts;
    }

    /** @param {string[]} opts @returns {NvgdVariant | undefined} */
    function matchVariant(opts) {
      return variants.find(function (v) {
        return v.options && v.options.length === opts.length &&
          v.options.every(function (o, i) { return String(o) === String(opts[i]); });
      });
    }

    function setActiveStates() {
      /** @type {NodeListOf<HTMLInputElement>} */
      (root.querySelectorAll('input[data-nvgd-option]')).forEach(function (inp) {
        const label = inp.nextElementSibling;
        if (label) label.classList.toggle('is-selected', inp.checked);
      });
    }

    function updateCurrentLabels() {
      /** @type {NodeListOf<HTMLElement>} */
      (root.querySelectorAll('[data-nvgd-optgroup]')).forEach(function (group) {
        var cur = group.querySelector('[data-nvgd-current]');
        var checked = /** @type {HTMLInputElement | null} */ (group.querySelector('input[data-nvgd-option]:checked'));
        if (cur && checked) cur.textContent = checked.value;
      });
    }

    /** @param {NvgdVariant | undefined} v */
    function applyVariant(v) {
      if (idInput && v) idInput.value = String(v.id);
      if (priceEl && v && v.price) priceEl.textContent = v.price;

      /* -- compare-at price + discount badge: show/hide based on variant -- */
      if (compareEl && v) {
        compareEl.textContent = v.compare || '';
        compareEl.style.display = v.compare ? '' : 'none';
      }
      if (discountEl && v) {
        discountEl.textContent = v.discount ? ('OFF -' + v.discount + '%') : '';
        discountEl.style.display = v.discount ? '' : 'none';
      }

      if (mainImg && v && v.media) mainImg.src = v.media;
      if (atcBtn) {
        var found = !!v;
        var avail = !!(v && v.available);
        atcBtn.disabled = !found || !avail;
        if (atcText) {
          atcText.textContent = (!found)
            ? (atcBtn.getAttribute('data-unavailable-label') || 'Unavailable')
            : (avail ? (atcBtn.getAttribute('data-add-label') || 'Add to cart')
                     : (atcBtn.getAttribute('data-soldout-label') || 'Sold out'));
        }
      }
      if (v && window.history && history.replaceState) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', String(v.id));
          history.replaceState({}, '', url);
        } catch (e) { /* noop */ }
      }
    }

    /** @type {NodeListOf<HTMLInputElement>} */
    (root.querySelectorAll('input[data-nvgd-option]')).forEach(function (inp) {
      inp.addEventListener('change', function () {
        setActiveStates();
        updateCurrentLabels();
        if (!variants.length) return; // STATIC demo: visual feedback only
        var v = matchVariant(selectedOptions());
        applyVariant(v);
        if (v) announce((v.available ? 'Selected ' : 'Unavailable: ') + v.options.join(', '));
      });
    });
    setActiveStates();

    /* ---- quantity stepper -------------------------------------------------- */
    var qtyInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-nvgd-qty]'));
    /** @type {NodeListOf<HTMLElement>} */
    (root.querySelectorAll('[data-nvgd-qty-step]')).forEach(function (b) {
      b.addEventListener('click', function () {
        if (!qtyInput) return;
        var dir = parseInt(b.getAttribute('data-nvgd-qty-step') || '0', 10) || 0;
        var val = (parseInt(qtyInput.value, 10) || 1) + dir;
        if (val < 1) val = 1;
        qtyInput.value = String(val);
      });
    });

    /* ---- gallery thumbnail swap ------------------------------------------- */
    /** @type {NodeListOf<HTMLElement>} */
    var thumbs = root.querySelectorAll('[data-nvgd-thumb]');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-nvgd-thumb');
        if (src && mainImg) mainImg.src = src;
        thumbs.forEach(function (t) { t.removeAttribute('aria-current'); t.classList.remove('is-active'); });
        thumb.setAttribute('aria-current', 'true');
        thumb.classList.add('is-active');
      });
    });

    /* ---- AJAX add to cart (native-equivalent flow) ------------------------- */
    if (form && atcBtn && variants.length) {
      atcBtn.addEventListener('click', function (e) {
        if (atcBtn.disabled) return;
        e.preventDefault();
        addToCart(form, atcBtn);
      });
    }

    /** @returns {any} */
    function routes() {
      return (typeof Theme !== 'undefined' && Theme.routes) || {};
    }

    /** @param {HTMLFormElement} theForm @param {HTMLButtonElement} btn */
    function addToCart(theForm, btn) {
      var fd = new FormData(theForm);
      /** @type {string[]} */
      var sectionIds = [];
      /** @type {NodeListOf<HTMLElement>} */
      (document.querySelectorAll('cart-items-component')).forEach(function (el) {
        if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });
      if (sectionIds.length) fd.set('sections', sectionIds.join(','));

      var addUrl = routes().cart_add_url || '/cart/add';
      btn.setAttribute('aria-busy', 'true');

      fetch(addUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: fd
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.status) {            // cart error (e.g. sold out)
            btn.removeAttribute('aria-busy');
            announce(res.message || 'Could not add to cart');
            return undefined;
          }
          var cartUrl = (routes().cart_url || '/cart') + '.js';
          return fetch(cartUrl).then(function (r) { return r.json(); }).then(function (cart) {
            btn.removeAttribute('aria-busy');
            announce('Added to cart');
            try {
              document.dispatchEvent(new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: root.id,
                  data: {
                    source: 'nvgd-product',
                    itemCount: cart && cart.item_count,
                    sections: res && res.sections,
                    variantId: fd.get('id')
                  }
                }
              }));
            } catch (e) { /* noop */ }
            var drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
            if (drawer && typeof drawer.open === 'function') drawer.open();
            else if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
            else window.location.href = routes().cart_url || '/cart';
          });
        })
        .catch(function () {
          btn.removeAttribute('aria-busy');
          if (theForm && typeof theForm.submit === 'function') theForm.submit(); // hard fallback
        });
    }
  }

  function boot() {
    /** @type {NodeListOf<HTMLElement>} */
    (document.querySelectorAll('[data-nvgd="product"]')).forEach(init);
  }


  /* Auto-init kalau section ini di-inject ulang secara dinamis,
   contohnya saat di-clone ke dalam quick-add modal. */
if (typeof MutationObserver !== 'undefined') {
  var nvgdObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches && node.matches('[data-nvgd="product"]')) init(node);
        if (node.querySelectorAll) {
          node.querySelectorAll('[data-nvgd="product"]').forEach(init);
        }
      });
    });
  });
  nvgdObserver.observe(document.body, { childList: true, subtree: true });
}

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor: re-init when the section is re-rendered */
  document.addEventListener('shopify:section:load', function (e) {
    var target = /** @type {HTMLElement} */ (e.target);
    var sec = target && target.querySelector ? /** @type {HTMLElement | null} */ (target.querySelector('[data-nvgd="product"]')) : null;
    if (sec) { sec.dataset.nvgdInit = ''; init(sec); }
  });
})();
